/* Matching Set module — see Matching-Set-Build-Brief.md §3.
   Cart-aware free-shipping bar (policy framing), two-way synced size selector,
   auto-upgrade to the set, single multi-item add-to-cart. */

if (!customElements.get('matching-set-module')) {
  class MatchingSetModule extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-msm-config]').textContent);
      } catch (e) {
        this.hidden = true;
        return;
      }

      this.els = {
        bar: this.querySelector('[data-msm-bar]'),
        barAmount: this.querySelector('[data-msm-bar-amount]'),
        barPrefix: this.querySelector('[data-msm-bar-prefix]'),
        barCheck: this.querySelector('[data-msm-bar-check]'),
        barFill: this.querySelector('[data-msm-bar-fill]'),
        upgradeMsg: this.querySelector('[data-msm-upgrade-msg]'),
        setCard: this.querySelector('[data-msm-set-card]'),
        setCheck: this.querySelector('[data-msm-check="set"]'),
        ringCheck: this.querySelector('[data-msm-check="ring"]'),
        scrunchieCheck: this.querySelector('[data-msm-check="scrunchie"]'),
        ringRow: this.querySelector('[data-msm-row="ring"]'),
        scrunchieRow: this.querySelector('[data-msm-row="scrunchie"]'),
        sizeSelect: this.querySelector('[data-msm-size]'),
        sizeLine: this.querySelector('[data-msm-size-line]'),
        setPrice: this.querySelector('[data-msm-set-price]'),
        setCompare: this.querySelector('[data-msm-set-compare]'),
        cta: this.querySelector('[data-msm-cta]'),
        ctaAction: this.querySelector('[data-msm-cta-action]'),
        ctaSub: this.querySelector('[data-msm-cta-sub]'),
        error: this.querySelector('[data-msm-error]'),
        live: this.querySelector('[data-msm-live]')
      };

      this.state = {
        size: this.dataset.initialSize || null,
        cartTotal: 0
      };

      this.boundVariantChange = this.onMainVariantChange.bind(this);
      this.boundCartChange = this.refreshCartTotal.bind(this);
      document.addEventListener('on:variant:change', this.boundVariantChange);
      document.addEventListener('on:cart:add', this.boundCartChange);
      document.addEventListener('on:cart:change', this.boundCartChange);
      // Follow the main picker the moment a size is picked (not after the section
      // re-render), so this module's Add button never uses the previous size.
      this.boundMainPickerChange = this.onMainPickerChange.bind(this);
      this.listenRoot = this.closest('.shopify-section') || document;
      this.listenRoot.addEventListener('change', this.boundMainPickerChange);

      if (this.els.sizeSelect) {
        this.els.sizeSelect.addEventListener('change', () => {
          this.setSize(this.els.sizeSelect.value, false);
        });
      }
      ['setCheck', 'ringCheck', 'scrunchieCheck'].forEach((key) => {
        if (this.els[key]) this.els[key].addEventListener('change', this.onCheckChange.bind(this));
      });
      this.els.cta.addEventListener('click', this.addToCart.bind(this));

      this.refreshCartTotal();
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.boundVariantChange);
      document.removeEventListener('on:cart:add', this.boundCartChange);
      document.removeEventListener('on:cart:change', this.boundCartChange);
      if (this.listenRoot) this.listenRoot.removeEventListener('change', this.boundMainPickerChange);
    }

    /* ---------- data helpers ---------- */

    caseVariant() {
      return this.config.caseVariants.find((v) => v.size === this.state.size) || null;
    }

    setVariant() {
      if (!this.config.set) return null;
      return this.config.set.variants.find((v) => v.size === this.state.size) || null;
    }

    setChecked() {
      return !!(this.els.setCheck && this.els.setCheck.checked);
    }

    money(cents) {
      const amount = (cents / 100).toFixed(2);
      const format = this.config.moneyFormat || '${{amount}}';
      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/<[^>]*>/g, '');
    }

    sizeShort() {
      return this.state.size ? this.state.size.replace(/^For\s+/i, '') : '';
    }

    announce(text) {
      if (!this.els.live) return;
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        this.els.live.textContent = '';
        requestAnimationFrame(() => { this.els.live.textContent = text; });
      }, 400);
    }

    /* ---------- size sync (two-way, loop-guarded) ---------- */

    setSize(size, fromMainPicker) {
      if (!size || size === this.state.size) return;
      this.state.size = size;
      if (this.els.sizeSelect && this.els.sizeSelect.value !== size) {
        this.els.sizeSelect.value = size;
      }
      if (!fromMainPicker) this.syncMainPicker(size);
      this.render();
    }

    onMainVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant) return;
      const size = variant.option1;
      if (this.config.caseVariants.some((v) => v.size === size)) {
        this.setSize(size, true);
      }
    }

    onMainPickerChange(evt) {
      if (!evt.target.closest || !evt.target.closest('variant-picker')) return;
      if (!evt.target.closest(`[data-option="${CSS.escape(this.config.optionName)}"]`)) return;
      const size = (evt.detail && evt.detail.selectedValue)
        || (evt.target.matches('input[type="radio"]') ? evt.target.value : null);
      if (size && this.config.caseVariants.some((v) => v.size === size)) {
        this.setSize(size, true);
      }
    }

    syncMainPicker(size) {
      const section = this.closest('.shopify-section') || document;
      const picker = section.querySelector('variant-picker');
      if (!picker) return;
      const selector = picker.querySelector(`[data-option="${CSS.escape(this.config.optionName)}"]`);
      if (!selector) return;

      if (selector.dataset.selectorType === 'dropdown') {
        const option = selector.querySelector(`.custom-select__option[data-value="${CSS.escape(size)}"]:not([aria-selected="true"])`);
        if (option) {
          const customSelect = option.closest('custom-select');
          const convertEvent = (evt) => {
            evt.detail.noFocus = true;
            customSelect.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail }));
          };
          customSelect.addEventListener('change', convertEvent, { once: true });
          customSelect.selectOption(option);
        }
      } else {
        const input = selector.querySelector(`input[value="${CSS.escape(size)}"]:not(:checked)`);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } }));
        }
      }
    }

    /* ---------- cart awareness ---------- */

    refreshCartTotal() {
      fetch(`${(window.theme && theme.routes && theme.routes.cart) || '/cart'}.js`.replace('.js.js', '.js'))
        .then((r) => r.json())
        .then((cart) => {
          this.state.cartTotal = cart.total_price || 0;
          this.render();
        })
        .catch(() => {});
    }

    /* ---------- selection logic ---------- */

    onCheckChange(evt) {
      const which = evt.target.dataset.msmCheck;

      if (which === 'set') {
        if (!this.setChecked()) this.els.upgradeMsg.hidden = true;
      } else {
        // Auto-upgrade: ring + scrunchie both ticked = the set's contents.
        const ring = this.els.ringCheck && this.els.ringCheck.checked;
        const scrunchie = this.els.scrunchieCheck && this.els.scrunchieCheck.checked;
        const sv = this.setVariant();
        if (ring && scrunchie && sv && sv.available && this.els.setCheck) {
          this.els.ringCheck.checked = false;
          this.els.scrunchieCheck.checked = false;
          this.els.setCheck.checked = true;
          this.els.upgradeMsg.hidden = false;
          const save = sv.compareAt > sv.price ? this.money(sv.compareAt - sv.price) : this.money(1500);
          this.announce(`That's the full set — you just saved ${save}. ${this.ctaSummaryText()}`);
          this.render();
          return;
        }
      }

      this.render();
      this.announce(this.ctaSummaryText());
    }

    ctaSummaryText() {
      const sub = this.els.ctaSub ? this.els.ctaSub.textContent : '';
      const bar = this.barText();
      return `${sub}. ${bar}`;
    }

    barText() {
      const total = this.projectedTotal();
      const met = total >= this.config.threshold;
      const prefix = this.setChecked() ? 'with this set' : 'with this case';
      return `Orders over $50 ship FREE — ${prefix} you're at ${this.money(total)}${met ? ', free shipping threshold reached' : ''}`;
    }

    projectedTotal() {
      let total = this.state.cartTotal;
      if (this.setChecked()) {
        const sv = this.setVariant();
        if (sv) total += sv.price;
      } else {
        const cv = this.caseVariant();
        if (cv) total += cv.price;
        if (this.els.ringCheck && this.els.ringCheck.checked && this.config.ring) total += this.config.ring.price;
        if (this.els.scrunchieCheck && this.els.scrunchieCheck.checked && this.config.scrunchie) total += this.config.scrunchie.price;
      }
      return total;
    }

    /* ---------- render ---------- */

    render() {
      const cv = this.caseVariant();
      const sv = this.setVariant();
      const setSelected = this.setChecked();

      // Set card visibility per size (component-linked inventory).
      if (this.els.setCard) {
        const cardAvailable = !!(sv && sv.available);
        this.els.setCard.hidden = !cardAvailable;
        if (!cardAvailable && setSelected) {
          this.els.setCheck.checked = false;
          this.els.upgradeMsg.hidden = true;
        }
        this.els.setCard.classList.toggle('msm__card--checked', this.setChecked());
        if (sv) {
          if (this.els.setPrice) this.els.setPrice.textContent = this.money(sv.price);
          if (this.els.setCompare && sv.compareAt > sv.price) this.els.setCompare.textContent = this.money(sv.compareAt);
        }
      }

      // Dim + disable add-on rows while the set is selected (the set contains them).
      const dim = this.setChecked();
      [['ringRow', 'ringCheck', 'ring'], ['scrunchieRow', 'scrunchieCheck', 'scrunchie']].forEach(([rowKey, checkKey, cfgKey]) => {
        const row = this.els[rowKey];
        const check = this.els[checkKey];
        const cfg = this.config[cfgKey];
        if (!row || !check || !cfg) return;
        row.classList.toggle('msm__row--dimmed', dim);
        check.disabled = dim || !cfg.available;
      });

      // Free-shipping bar (policy framing only — never promise language).
      const total = this.projectedTotal();
      const met = total >= this.config.threshold;
      const pct = Math.min(100, Math.round((total / this.config.threshold) * 100));
      if (this.els.barFill) this.els.barFill.style.width = `${pct}%`;
      if (this.els.barPrefix) this.els.barPrefix.textContent = this.setChecked() ? 'with this set' : 'with this case';
      if (this.els.barAmount) this.els.barAmount.textContent = this.money(total);
      if (this.els.barCheck) this.els.barCheck.hidden = !met;
      if (this.els.bar) this.els.bar.classList.toggle('msm__bar--met', met);

      // CTA: action line names the live size; subline NAMES the items, never counts them.
      const size = this.sizeShort();
      if (this.els.sizeLine) {
        this.els.sizeLine.textContent = this.state.size ? `Your size: ${size}` : 'Your size: select above';
      }
      if (!this.state.size) {
        this.els.cta.disabled = true;
        this.els.ctaAction.textContent = 'Add to cart';
        this.els.ctaSub.textContent = 'Select a size above';
      } else if (this.setChecked() && sv) {
        this.els.cta.disabled = !sv.available;
        this.els.ctaAction.textContent = `Add the set — ${size}`;
        const save = sv.compareAt > sv.price ? ` · save ${this.money(sv.compareAt - sv.price)}` : '';
        this.els.ctaSub.textContent = `${this.money(sv.price)}${save}`;
      } else {
        this.els.cta.disabled = !(cv && cv.available);
        this.els.ctaAction.textContent = `Add to cart — ${size}`;
        const parts = ['Case'];
        if (this.els.ringCheck && this.els.ringCheck.checked && this.config.ring) parts.push(this.config.ring.label);
        if (this.els.scrunchieCheck && this.els.scrunchieCheck.checked && this.config.scrunchie) parts.push(this.config.scrunchie.label);
        this.els.ctaSub.textContent = `${parts.join(' + ')} · ${this.money(total - this.state.cartTotal)}`;
      }
    }

    /* ---------- add to cart ---------- */

    async addToCart() {
      if (this.els.cta.disabled || this.els.cta.classList.contains('is-loading')) return;

      const items = [];
      if (this.setChecked()) {
        const sv = this.setVariant();
        if (!sv) return;
        items.push({ id: sv.id, quantity: 1 });
      } else {
        const cv = this.caseVariant();
        if (!cv) return;
        items.push({ id: cv.id, quantity: 1 });
        if (this.els.ringCheck && this.els.ringCheck.checked && this.config.ring) items.push({ id: this.config.ring.id, quantity: 1 });
        if (this.els.scrunchieCheck && this.els.scrunchieCheck.checked && this.config.scrunchie) items.push({ id: this.config.scrunchie.id, quantity: 1 });
      }

      this.els.error.hidden = true;
      this.els.cta.classList.add('is-loading');
      this.els.cta.setAttribute('aria-disabled', 'true');

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el && el.dataset.sectionId);

      try {
        const response = await fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items,
            sections: sectionsToUpdate.map((el) => el.dataset.sectionId).join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(typeof data.description === 'string' ? data.description : (data.message || 'Unable to add to cart'));
        }

        if (data.sections) {
          sectionsToUpdate.forEach((el) => {
            if (data.sections[el.dataset.sectionId] && typeof el.updateFromCartChange === 'function') {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: items[0].id } }));

        // Reset selections — the items are in the cart now; the bar keeps tracking them via /cart.js.
        if (this.els.setCheck) this.els.setCheck.checked = false;
        if (this.els.ringCheck && !this.els.ringCheck.disabled) this.els.ringCheck.checked = false;
        if (this.els.scrunchieCheck && !this.els.scrunchieCheck.disabled) this.els.scrunchieCheck.checked = false;
        this.els.upgradeMsg.hidden = true;
        this.announce('Added to cart');
        this.refreshCartTotal();

        if (window.AMP_API && typeof AMP_API.OPEN_CART === 'function') {
          // The store's cart drawer is the AMP slide cart.
          AMP_API.OPEN_CART();
        } else if (window.theme && theme.settings && theme.settings.afterAddToCart === 'drawer') {
          const drawer = document.querySelector('.js-cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open(this.els.cta);
        }
      } catch (error) {
        this.els.error.textContent = error.message || 'Unable to add to cart. Please try again.';
        this.els.error.hidden = false;
        this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: this.els.error.textContent } }));
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } finally {
        this.els.cta.classList.remove('is-loading');
        this.els.cta.removeAttribute('aria-disabled');
        this.render();
      }
    }
  }

  customElements.define('matching-set-module', MatchingSetModule);
}
